/**
 * State minimum total nursing HPRD (hours per resident day).
 *
 * Manually maintained config — expected to change over the coming weeks as we
 * refine each state's statutory floor. Values seeded from the analytics pipeline
 * `get_state_validation_rules()` in:
 *   analytics/snf_understaffing/pipeline/step2_validations/run_validations.py
 * (the `hprd_threshold` field, which reflects each state's CURRENT total
 * direct-care nursing minimum — RN + LPN + CNA combined).
 *
 * Conventions:
 *   number → the state's minimum total nursing HPRD (drawn as a line on the
 *            Staffing Trend chart).
 *   null   → no single total-HPRD floor we track (e.g. NJ uses shift-based
 *            ratios, not an HPRD floor) OR not yet researched. No line is drawn.
 *
 * NOTE: There is NO federal HPRD minimum — the CMS 3.5 proposed rule was never
 * finalized. Where a state phased in higher thresholds over time, the value
 * below is the latest/current floor.
 */
export const STATE_MIN_HPRD: Record<string, number | null> = {
  AL: null,
  AK: null,
  AZ: null,
  AR: null,
  CA: 3.5,   // 24-hr RN coverage also required
  CO: 2.0,
  CT: 3.0,   // 1.9 before 2024-01-01
  DC: 4.1,
  DE: 3.67,  // 3.28 through 2024-12-31
  FL: 3.6,
  GA: 2.0,
  HI: null,
  ID: 2.4,
  IL: 2.5,   // intermediate-care floor; 3.8 skilled
  IN: null,
  IA: null,
  KS: 1.85,
  KY: null,
  LA: 2.35,
  ME: null,
  MD: 3.0,
  MA: 3.58,  // RN >= 0.508 HPPD separately
  MI: 2.25,
  MN: 2.0,
  MS: 2.8,
  MO: null,
  MT: null,
  NE: null,
  NV: null,
  NH: null,
  NJ: null,  // shift-based ratios, no single HPRD floor
  NM: null,
  NY: 3.5,   // CNA >= 2.2, licensed (RN+LPN) >= 1.1 HPPD
  NC: null,
  ND: null,
  OH: 2.5,
  OK: 2.9,
  OR: null,
  PA: 3.2,   // phased up from 2.7 (pre-2023-07) and 2.87
  RI: 3.81,
  SC: null,
  SD: null,
  TN: 2.0,
  TX: null,
  UT: null,
  VT: 3.0,   // 2.0 CNA/LNA floor
  VA: null,
  WA: 3.4,
  WV: 2.25,
  WI: 2.0,
  WY: 1.5,
};

/**
 * Look up a state's minimum total nursing HPRD.
 * Returns the number, or null when we have no tracked floor for that state.
 */
export function getStateMinHprd(state: string | null | undefined): number | null {
  if (!state) return null;
  return STATE_MIN_HPRD[state.toUpperCase()] ?? null;
}
